import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Save, LogOut, Building2, Plus, Star, Trash2, CreditCard, ExternalLink, KeyRound } from 'lucide-react';
import axios from 'axios';
import { sanitizeOrgNumber } from '../../utils/inputSanitizers';
import { API } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export function BusinessProfile() {
  const { t } = useTranslation();
  const { headers, user, logout } = useAuthContext();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ company_name: '', org_number: '', address: '' });

  // Swish accounts state
  const [swishAccounts, setSwishAccounts] = useState([]);
  const [addSwishOpen, setAddSwishOpen] = useState(false);
  const [newMerchant, setNewMerchant] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [addingSwish, setAddingSwish] = useState(false);
  const nav = useNavigate();

  // Subscription
  const [subscription, setSubscription] = useState(null);

  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    axios.get(`${API}/business/profile`, { headers })
      .then(r => {
        setProfile(r.data);
        setForm({
          company_name: r.data.company_name || '',
          org_number: r.data.org_number || '',
          address: r.data.address || '',
        });
      })
      .catch(e => console.error(e));
    fetchSwishAccounts();
    axios.get(`${API}/subscription/current`, { headers })
      .then(r => setSubscription(r.data))
      .catch(e => console.error('Failed to fetch subscription', e));
  }, [headers]);

  const fetchSwishAccounts = async () => {
    try {
      const res = await axios.get(`${API}/business/swish-accounts`, { headers });
      setSwishAccounts(res.data.items || []);
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/business/profile`, form, { headers });
      toast.success(t('business.profileUpdated'));
      setEditing(false);
    } catch (e) {
      toast.error(t('business.failedToUpdate'));
    }
  };

  const handleAddSwish = async () => {
    if (!newMerchant.trim()) return toast.error(t('auth.swishMerchantNumber') + ' is required');
    setAddingSwish(true);
    try {
      await axios.post(`${API}/business/swish-accounts`, {
        merchant_number: newMerchant.trim(),
        display_name: newDisplayName.trim() || newMerchant.trim(),
        is_primary: swishAccounts.length === 0,
      }, { headers });
      toast.success(t('auth.swishMerchantNumber') + ' added');
      setAddSwishOpen(false);
      setNewMerchant('');
      setNewDisplayName('');
      fetchSwishAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally {
      setAddingSwish(false);
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      await axios.put(`${API}/business/swish-accounts/${id}/set-primary`, {}, { headers });
      toast.success(t('auth.setPrimary'));
      fetchSwishAccounts();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteSwish = async (id) => {
    try {
      await axios.delete(`${API}/business/swish-accounts/${id}`, { headers });
      toast.success('Removed');
      fetchSwishAccounts();
    } catch (e) { toast.error('Failed'); }
  };

  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t('profile.passwordsDoNotMatch', { defaultValue: 'Passwords do not match' }));
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error(t('profile.passwordMinLength', { defaultValue: 'Password must be at least 6 characters' }));
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, { headers });
      toast.success(t('profile.passwordChanged', { defaultValue: 'Password changed. Please log in again.' }));
      setShowPasswordChange(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      await logout();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('profile.failedToChangePassword', { defaultValue: 'Failed to change password' }));
    } finally {
      setChangingPassword(false);
    }
  };

  if (!profile) return <div className="text-center py-8">{t('common.loading')}</div>;

  return (
    <div className="space-y-4" data-testid="business-profile">
      <h2 className="text-lg font-semibold">{t('business.profile')}</h2>

      {/* Company Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-base">{profile.company_name || t('business.yourBusiness')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>{t('business.companyName')}</Label>
            <Input disabled={!editing} value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} data-testid="company-name-input" />
          </div>
          <div>
            <Label>{t('business.orgNumber')}</Label>
            <Input disabled={!editing} value={form.org_number} onChange={e => setForm(f => ({ ...f, org_number: sanitizeOrgNumber(e.target.value) }))} data-testid="orgnumber-input-business-profile" />
          </div>
          <div>
            <Label>{t('business.address')}</Label>
            <Input disabled={!editing} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} data-testid="address-input" />
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>{t('business.email')}: {profile.email}</p>
            <p>{t('business.paymigId')}: {profile.paymig_id || t('common.na')}</p>
            <p>{t('business.accountOwner')}: {profile.full_name}</p>
          </div>

          <div className="flex gap-2 pt-2">
            {editing ? (
              <>
                <Button onClick={handleSave} data-testid="save-profile-btn"><Save className="h-4 w-4 mr-1" /> {t('common.save')}</Button>
                <Button variant="outline" onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-profile-btn">{t('business.editProfile')}</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subscription Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">{t('profile.subscription', { defaultValue: 'Subscription' })}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="business-subscription">
          <div className="flex justify-between items-center p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border">
            <div>
              <div className="font-bold text-lg flex items-center gap-2">
                {subscription?.current_plan?.name || t('profile.free', { defaultValue: 'Free' })}
                {Number(subscription?.current_plan?.cost_monthly || 0) === 0 ? (
                  <Badge variant="secondary">{t('profile.free', { defaultValue: 'Free' })}</Badge>
                ) : (
                  <Badge variant="default">{subscription?.current_plan?.cost_monthly} SEK/{t('profile.month', { defaultValue: 'mo' })}</Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {subscription?.current_plan?.max_transactions === -1
                  ? t('profile.unlimited', { defaultValue: 'Unlimited transactions' })
                  : `${subscription?.current_plan?.max_transactions ?? '-'} ${t('profile.transactions', { defaultValue: 'transactions' })}`}
              </div>
              {subscription?.subscription_end && (
                <div className="text-xs text-blue-600 mt-2">
                  {t('profile.renewsOn', { defaultValue: 'Renews on' })}: {new Date(subscription.subscription_end).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
          <Button onClick={() => nav('/subscription-portal')} variant="outline" className="w-full gap-2" data-testid="manage-subscription-btn">
            <ExternalLink className="h-4 w-4" />
            {t('profile.manageSubscription', { defaultValue: 'Manage subscription' })}
          </Button>
        </CardContent>
      </Card>

      {/* Swish Merchant Accounts Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-base">{t('auth.swishMerchantNumber')}s</CardTitle>
            </div>
            <Button size="sm" onClick={() => setAddSwishOpen(true)} data-testid="add-swish-btn">
              <Plus className="h-4 w-4 mr-1" /> {t('common.add')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {swishAccounts.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground" data-testid="no-swish-accounts">
              <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No Swish merchant accounts added yet</p>
              <p className="text-xs mt-1">Add your first Swish merchant number to start receiving payments</p>
            </div>
          ) : (
            <div className="space-y-2">
              {swishAccounts.map(acc => (
                <div
                  key={acc.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${acc.is_primary ? 'border-teal-300 bg-teal-50' : 'border-gray-200 bg-white'}`}
                  data-testid={`swish-account-${acc.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${acc.is_primary ? 'bg-teal-100' : 'bg-gray-100'}`}>
                      <CreditCard className={`h-4 w-4 ${acc.is_primary ? 'text-teal-700' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-sm">{acc.merchant_number}</span>
                        {acc.is_primary && (
                          <Badge className="bg-teal-100 text-teal-700 text-[10px]" data-testid={`primary-badge-${acc.id}`}>
                            <Star className="h-3 w-3 mr-0.5 fill-current" /> {t('auth.primary')}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{acc.display_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!acc.is_primary && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => handleSetPrimary(acc.id)}
                        data-testid={`set-primary-${acc.id}`}
                      >
                        <Star className="h-3 w-3 mr-1" /> {t('auth.setPrimary')}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-rose-500 hover:text-rose-700"
                      onClick={() => handleDeleteSwish(acc.id)}
                      data-testid={`delete-swish-${acc.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Swish Dialog */}
      <Dialog open={addSwishOpen} onOpenChange={setAddSwishOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('auth.addSwishMerchant')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{t('auth.swishMerchantNumber')} *</Label>
              <Input
                data-testid="new-swish-merchant-input"
                value={newMerchant}
                onChange={(e) => setNewMerchant(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder={t('auth.swishMerchantPlaceholder')}
              />
              <p className="text-xs text-muted-foreground mt-1">Must start with 123 (10-11 digits)</p>
            </div>
            <div>
              <Label>Display Name</Label>
              <Input
                data-testid="new-swish-display-name"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="e.g., Main Account, Events"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSwishOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAddSwish} disabled={addingSwish || !newMerchant.trim()} data-testid="confirm-add-swish">
              {addingSwish ? '...' : t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-slate-600" />
            <CardTitle className="text-base">{t('settings.security', { defaultValue: 'Security' })}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="business-security">
          {!showPasswordChange ? (
            <Button variant="outline" onClick={() => setShowPasswordChange(true)} data-testid="change-password-btn">
              {t('settings.changePassword', { defaultValue: 'Change password' })}
            </Button>
          ) : (
            <>
              <div>
                <Label>{t('profile.currentPassword', { defaultValue: 'Current password' })}</Label>
                <Input type="password" value={passwordForm.current_password}
                  onChange={e => setPasswordForm(f => ({ ...f, current_password: e.target.value }))} data-testid="current-password-input" />
              </div>
              <div>
                <Label>{t('profile.newPassword', { defaultValue: 'New password' })}</Label>
                <Input type="password" value={passwordForm.new_password}
                  onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} data-testid="new-password-input" />
              </div>
              <div>
                <Label>{t('profile.confirmPassword', { defaultValue: 'Confirm new password' })}</Label>
                <Input type="password" value={passwordForm.confirm_password}
                  onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))} data-testid="confirm-password-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={changePassword} disabled={changingPassword} data-testid="save-password-btn">
                  {changingPassword ? '...' : t('common.save', { defaultValue: 'Save' })}
                </Button>
                <Button variant="outline" onClick={() => { setShowPasswordChange(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}>
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Button variant="destructive" className="w-full" onClick={logout} data-testid="logout-btn">
        <LogOut className="h-4 w-4 mr-1" /> {t('auth.logout')}
      </Button>
    </div>
  );
}
